import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Switch
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  AccountBalance,
  Code,
  Public,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import config from '../../../config';
import axios from 'axios';
import { useConfirm } from '../../../hooks/useConfirm';

const translations = {
  ar: {
    title: 'إدارة البنوك',
    subtitle: 'إضافة وتعديل بيانات البنوك والحسابات المصرفية',
    addBank: 'إضافة بنك',
    editBank: 'تعديل بنك',
    bankNameEn: 'اسم البنك (English)',
    bankNameAr: 'اسم البنك (عربي)',
    bankCode: 'رمز البنك',
    swiftCode: 'كود SWIFT',
    shortName: 'الاسم المختصر',
    isActive: 'نشط',
    status: 'الحالة',
    actions: 'الإجراءات',
    active: 'نشط',
    inactive: 'غير نشط',
    cancel: 'إلغاء',
    save: 'حفظ',
    deleteConfirm: 'هل أنت متأكد من حذف هذا البنك؟',
    saveSuccess: 'تم حفظ البنك بنجاح',
    saveError: 'فشل حفظ البنك',
    deleteSuccess: 'تم حذف البنك بنجاح',
    deleteError: 'فشل حذف البنك',
    loadError: 'فشل تحميل البنوك',
    noData: 'لا توجد بنوك',
    totalBanks: 'إجمالي البنوك'
  },
  en: {
    title: 'Banks Management',
    subtitle: 'Add and manage bank accounts and banking information',
    addBank: 'Add Bank',
    editBank: 'Edit Bank',
    bankNameEn: 'Bank Name (English)',
    bankNameAr: 'Bank Name (Arabic)',
    bankCode: 'Bank Code',
    swiftCode: 'SWIFT Code',
    shortName: 'Short Name',
    isActive: 'Active',
    status: 'Status',
    actions: 'Actions',
    active: 'Active',
    inactive: 'Inactive',
    cancel: 'Cancel',
    save: 'Save',
    deleteConfirm: 'Are you sure you want to delete this bank?',
    saveSuccess: 'Bank saved successfully',
    saveError: 'Failed to save bank',
    deleteSuccess: 'Bank deleted successfully',
    deleteError: 'Failed to delete bank',
    loadError: 'Failed to load banks',
    noData: 'No banks found',
    totalBanks: 'Total Banks'
  }
};

interface Bank {
  bankId: number;
  bankName: string;
  bankNameAr: string;
  bankCode: string;
  swiftCode: string;
  shortName: string;
  isActive: boolean;
}

export default function BanksManagementTab() {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBank, setEditingBank] = useState<Bank | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    bankName: '',
    bankNameAr: '',
    bankCode: '',
    swiftCode: '',
    shortName: '',
    isActive: true
  });

  useEffect(() => {
    loadBanks();
  }, []);

  const loadBanks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_BASE_URL}/Banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanks(response.data);
    } catch (error) {
      setMessage({ type: 'error', text: t.loadError });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (bank?: Bank) => {
    if (bank) {
      setEditingBank(bank);
      setFormData({
        bankName: bank.bankName,
        bankNameAr: bank.bankNameAr,
        bankCode: bank.bankCode,
        swiftCode: bank.swiftCode,
        shortName: bank.shortName,
        isActive: bank.isActive
      });
    } else {
      setEditingBank(null);
      setFormData({
        bankName: '',
        bankNameAr: '',
        bankCode: '',
        swiftCode: '',
        shortName: '',
        isActive: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBank(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');

      if (editingBank) {
        await axios.put(
          `${config.API_BASE_URL}/Banks/${editingBank.bankId}`,
          { ...editingBank, ...formData },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(`${config.API_BASE_URL}/Banks`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setMessage({ type: 'success', text: t.saveSuccess });
      handleCloseDialog();
      loadBanks();
    } catch (error: any) {
      setMessage({ type: 'error', text: t.saveError });
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await confirm({ message: t.deleteConfirm, severity: 'error' }))) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${config.API_BASE_URL}/Banks/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: t.deleteSuccess });
      loadBanks();
    } catch (error: any) {
      setMessage({ type: 'error', text: t.deleteError });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          color: 'white',
          p: 4,
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 8px 32px rgba(17, 153, 142, 0.3)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <AccountBalance sx={{ fontSize: 40 }} />
          <Box flex={1}>
            <Typography variant="h4" fontWeight="bold">
              {t.title}
            </Typography>
            <Typography variant="body2" component="div" sx={{ opacity: 0.9, mt: 0.5 }}>
              {t.subtitle}
            </Typography>
          </Box>
          <Chip
            label={`${banks.length} ${t.totalBanks}`}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }}
          />
        </Stack>
      </Box>

      {message && (
        <Alert
          severity={message.type}
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* Add Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(17, 153, 142, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0d7a72 0%, #2dd364 100%)',
              boxShadow: '0 6px 20px rgba(17, 153, 142, 0.6)'
            }
          }}
        >
          {t.addBank}
        </Button>
      </Box>

      {/* Table Card */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(17, 153, 142, 0.1)' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {t.bankNameEn}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {t.bankNameAr}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {t.bankCode}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {t.swiftCode}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {t.status}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {t.actions}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {banks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <AccountBalance sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                    <Typography color="text.secondary">{t.noData}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                banks.map((bank) => (
                  <TableRow
                    key={bank.bankId}
                    sx={{
                      '&:hover': { bgcolor: 'rgba(17, 153, 142, 0.05)' },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccountBalance sx={{ fontSize: 20, color: '#11998e' }} />
                        <Typography fontWeight="600">{bank.bankName}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography>{bank.bankNameAr}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bank.bankCode}
                        size="small"
                        icon={<Code sx={{ fontSize: 16 }} />}
                        sx={{
                          bgcolor: 'rgba(17, 153, 142, 0.1)',
                          color: '#11998e',
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bank.swiftCode}
                        size="small"
                        icon={<Public sx={{ fontSize: 16 }} />}
                        sx={{
                          bgcolor: 'rgba(56, 239, 125, 0.1)',
                          color: '#38ef7d',
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bank.isActive ? t.active : t.inactive}
                        color={bank.isActive ? 'success' : 'default'}
                        size="small"
                        icon={
                          bank.isActive ? (
                            <CheckCircle sx={{ fontSize: 16 }} />
                          ) : (
                            <Cancel sx={{ fontSize: 16 }} />
                          )
                        }
                        sx={{ minWidth: 90, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title={t.editBank}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(bank)}
                            sx={{
                              bgcolor: 'rgba(17, 153, 142, 0.1)',
                              '&:hover': { bgcolor: 'rgba(17, 153, 142, 0.2)' }
                            }}
                          >
                            <Edit sx={{ fontSize: 18, color: '#11998e' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.deleteConfirm}>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(bank.bankId)}
                            sx={{
                              bgcolor: 'rgba(244, 67, 54, 0.1)',
                              '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.2)' }
                            }}
                          >
                            <Delete sx={{ fontSize: 18, color: '#f44336' }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: 'white',
            fontWeight: 'bold'
          }}
        >
          {editingBank ? t.editBank : t.addBank}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label={t.bankNameEn}
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                label={t.bankNameAr}
                name="bankNameAr"
                value={formData.bankNameAr}
                onChange={handleChange}
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label={t.bankCode}
                name="bankCode"
                value={formData.bankCode}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <Code sx={{ mr: 1, color: '#11998e' }} />
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                label={t.swiftCode}
                name="swiftCode"
                value={formData.swiftCode}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <Public sx={{ mr: 1, color: '#38ef7d' }} />
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Stack>

            <TextField
              fullWidth
              label={t.shortName}
              name="shortName"
              value={formData.shortName}
              onChange={handleChange}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <Stack direction="row" alignItems="center" spacing={2}>
              <Switch
                checked={formData.isActive}
                onChange={handleChange}
                name="isActive"
                color="success"
              />
              <Typography>{t.isActive}</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
            {t.cancel}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              px: 3,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0d7a72 0%, #2dd364 100%)'
              }
            }}
          >
            {t.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}